/**
 * Registro de eventos do funil de cadastro/onboarding.
 *
 * Wrapper fino sobre a RPC `record_funnel_event`, por dois motivos:
 *  - componentes não devem chamar `supabase.rpc` direto (o vocabulário de
 *    eventos fica espalhado e um typo passa despercebido);
 *  - telemetria NUNCA pode quebrar o fluxo do usuário, então toda falha é
 *    engolida aqui, num único lugar, em vez de em cada call-site.
 *
 * O vocabulário é fechado dos dois lados: aqui pelo tipo `FunnelEvent`, e no
 * banco pelo `CHECK` da coluna `event_key`. `signup`, `email_confirmed` e
 * `first_login` NÃO estão neste tipo de propósito — são derivados no servidor
 * (trigger/backfill), porque `signup` acontece quando ainda não existe sessão e
 * `auth.uid()` seria nulo.
 */

import { supabase } from './supabase'

export type FunnelEvent =
  | 'onboarding_opened'
  | 'onboarding_step_completed'
  | 'onboarding_step_failed'
  | 'onboarding_dismissed'
  | 'onboarding_completed'
  | 'whatsapp_saved'
  // Perfil completo (card do dashboard + recompensa de trial):
  | 'profile_completed'
  | 'profile_reward_granted'
  | 'profile_nudge_dismissed'

/**
 * Grava o evento no banco e espelha no GA4.
 *
 * O espelho no GA4 é intencional e não é duplicação inútil: o banco responde
 * "o que aconteceu com ESTE usuário" (a pergunta do suporte, no modal do admin)
 * e o GA4 responde "qual passo derruba mais gente" (a pergunta do funil
 * agregado). Nenhum dos dois substitui o outro.
 */
export const recordFunnelEvent = async (
  event: FunnelEvent,
  step?: number | null,
  detail?: Record<string, unknown>
): Promise<void> => {
  try {
    window.gtag?.('event', event, { ...(detail ?? {}), ...(step != null ? { step } : {}) })
  } catch {
    // telemetria nunca quebra o fluxo
  }

  try {
    await supabase.rpc('record_funnel_event', {
      p_event_key: event,
      ...(step != null ? { p_step: step } : {}),
      ...(detail ? { p_detail: detail as never } : {}),
    })
  } catch {
    // idem: sem sessão, offline, RLS — nada disso justifica travar o usuário
  }
}

/** Versão fire-and-forget, para usar em handlers de UI sem `await`. */
export const trackFunnelEvent = (
  event: FunnelEvent,
  step?: number | null,
  detail?: Record<string, unknown>
): void => {
  void recordFunnelEvent(event, step, detail)
}
